/**
 * StatsSection Component
 * Displays practice statistics with sub-tab navigation (Practicing / Teaching / Activity).
 * When no practice data exists, shows general profile stats in a 3x2 grid per Figma spec.
 * Uses stat cards in a responsive grid layout with glass-morphism styling.
 */

import { useState } from "react"
import type { ProfileStatistics } from "@/features/profile/types/profile.types"
import { useTranslation } from "@/language"
import { cn } from "@/lib"
import type { PracticeStatistics, StatsSubTab } from "../types/my-rituality.types"
import { StatCard } from "./StatCard"

const scrollContainer =
  "overflow-x-auto scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"

interface StatsSectionProps {
  practiceStats?: PracticeStatistics | null
  profileStatistics?: ProfileStatistics | null
  isLoading?: boolean
}

export function StatsSection({ practiceStats, profileStatistics, isLoading }: StatsSectionProps) {
  const { t } = useTranslation("myRituality")
  const [activeSubTab, setActiveSubTab] = useState<StatsSubTab>("practicing")

  const hasPracticeData = Boolean(
    practiceStats?.practicingSince ||
      practiceStats?.ritualRhythm ||
      practiceStats?.mostPracticedRitual ||
      (practiceStats?.totalPracticeHours ?? 0) > 0,
  )

  const subTabs: { key: StatsSubTab; label: string }[] = [
    { key: "practicing", label: t("stats.practicing") },
    { key: "teaching", label: t("stats.teaching") },
    { key: "activity", label: t("stats.activity") },
  ]

  const formatHours = (hours: number): string => {
    if (hours < 1) return `${Math.round(hours * 60)}m`
    return `${Math.round(hours)}h`
  }

  if (isLoading) {
    return (
      <section className="mt-10">
        <div className="h-6 w-40 bg-white/5 rounded animate-pulse mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[120px] bg-white/5 rounded-[20px] animate-pulse" />
          ))}
        </div>
      </section>
    )
  }

  // Sub-tabs always shown; PracticingStats renders general profile stats when no practice data
  return (
    <section>
      {/* Section heading + sub-tabs (always shown) */}
      <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-2.5 md:gap-6">
        <h2 className="text-[18px] md:text-2xl font-semibold leading-7.5 uppercase opacity-90 text-primary">
          {t("stats.title")}
        </h2>
        <div className="flex items-center gap-5 h-7.5">
          {subTabs.map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveSubTab(tab.key)}
              className={cn(
                "text-sm font-semibold text-primary cursor-pointer transition-opacity uppercase",
                activeSubTab === tab.key ? "opacity-100" : "opacity-70",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards grid */}
      <div className="mt-2.5 md:mt-8">
        {activeSubTab === "practicing" && (
          <PracticingStats
            practiceStats={practiceStats}
            profileStatistics={profileStatistics}
            hasPracticeData={hasPracticeData}
            formatHours={formatHours}
          />
        )}
        {activeSubTab === "teaching" && <TeachingStats statistics={profileStatistics} />}
        {activeSubTab === "activity" && (
          <ActivityStats
            profileStatistics={profileStatistics}
            practiceStats={practiceStats}
            formatHours={formatHours}
          />
        )}
      </div>
    </section>
  )
}

function PracticingStats({
  practiceStats,
  formatHours,
}: {
  practiceStats?: PracticeStatistics | null
  profileStatistics?: ProfileStatistics | null
  hasPracticeData: boolean
  formatHours: (h: number) => string
}) {
  const { t } = useTranslation("myRituality")

  return (
    <div
      className={cn(
        "flex md:grid md:grid-cols-3 gap-3.75 md:gap-5 pb-2 md:pb-0 -mx-1 px-1 md:mx-0 md:px-0",
        scrollContainer,
      )}
    >
      <StatCard
        label={t("stats.practicingSince")}
        value={practiceStats?.practicingSince || "--"}
        description={t("stats.practicingSinceDescription")}
        className="shrink-0 w-[230px] md:w-auto md:col-span-1"
      />
      <StatCard
        label={t("stats.ritualRhythm")}
        value={practiceStats?.ritualRhythm || "--"}
        description={t("stats.ritualRhythmDescription")}
        className="shrink-0 w-[230px] md:w-auto md:col-span-1"
      />
      <StatCard
        label={t("stats.practicePattern")}
        value={formatHours(practiceStats?.totalPracticeHours ?? 0)}
        description={t("stats.practicePatternHoursDescription")}
        className="shrink-0 w-[230px] md:w-auto md:col-span-1"
      />
      <StatCard
        label={t("stats.mostPracticed")}
        value={practiceStats?.mostPracticedRitual?.title || "--"}
        description={t("stats.mostPracticedDescription")}
        className="shrink-0 w-[230px] md:w-auto md:col-span-2"
      />
      <StatCard
        label={t("stats.practicePattern")}
        value={practiceStats?.practicePatternDays || "--"}
        description={t("stats.practicePatternDaysDescription")}
        className="shrink-0 w-[230px] md:w-auto md:col-span-1"
      />
    </div>
  )
}

function TeachingStats({ statistics }: { statistics?: ProfileStatistics | null }) {
  const { t } = useTranslation("myRituality")

  return (
    <div
      className={cn(
        "flex md:grid md:grid-cols-3 gap-3.75 md:gap-5 pb-2 md:pb-0 -mx-1 px-1 md:mx-0 md:px-0",
        scrollContainer,
      )}
    >
      <StatCard
        label={t("teachingSubTabs.active")}
        value={String(statistics?.teachings ?? 0)}
        description={t("stats.teachingDescription")}
        className="shrink-0 w-[230px] md:w-auto"
      />
      <StatCard
        label={t("sessionsSubTabs.coaching")}
        value={String(statistics?.coachings.created ?? 0)}
        description={t("stats.coachingDescription")}
        className="shrink-0 w-[230px] md:w-auto"
      />
    </div>
  )
}

function ActivityStats({
  profileStatistics,
  practiceStats,
  formatHours,
}: {
  profileStatistics?: ProfileStatistics | null
  practiceStats?: PracticeStatistics | null
  formatHours: (h: number) => string
}) {
  const { t } = useTranslation("myRituality")

  return (
    <div
      className={cn(
        "flex md:grid md:grid-cols-3 gap-3.75 md:gap-5 pb-2 md:pb-0 -mx-1 px-1 md:mx-0 md:px-0",
        scrollContainer,
      )}
    >
      <StatCard
        label={t("stats.ritualsShared")}
        value={String(profileStatistics?.teachings ?? 0)}
        description={t("stats.ritualsSharedDescription")}
        className="shrink-0 w-[230px] md:w-auto"
      />
      <StatCard
        label={t("stats.followers")}
        value={String(profileStatistics?.followersCount ?? 0)}
        description={t("stats.followersDescription")}
        className="shrink-0 w-[230px] md:w-auto"
      />
      <StatCard
        label={t("stats.profileViews")}
        value={String(profileStatistics?.profileViews ?? 0)}
        description={t("stats.profileViewsDescription")}
        className="shrink-0 w-[230px] md:w-auto"
      />
      <StatCard
        label={t("stats.ritualsSaved")}
        value={String(profileStatistics?.savedRituals ?? 0)}
        description={t("stats.ritualsSavedDescription")}
        className="shrink-0 w-[230px] md:w-auto"
      />
      <StatCard
        label={t("stats.timeInRituality")}
        value={formatHours(practiceStats?.totalPracticeHours ?? 0)}
        description={t("stats.timeInRitualityDescription")}
        className="shrink-0 w-[230px] md:w-auto"
      />
      <StatCard
        label={t("stats.membership")}
        value={
          profileStatistics?.platformMembershipOrder != null
            ? `#${profileStatistics.platformMembershipOrder}`
            : "--"
        }
        description={t("stats.membershipDescription")}
        className="shrink-0 w-[230px] md:w-auto"
      />
    </div>
  )
}
